interface Merge {
  row: number;
  col: number;
  rowspan: number;
  colspan: number;
}

class MergeStore {
  public merges: Merge[] = [];
  private coverage: Map<string, number> = new Map();

  constructor(merges: Merge[] = []) {
    // Route through add() so overlap validation runs on load
    for (const m of merges) {
      this.add(m.row, m.col, m.rowspan, m.colspan);
    }
  }

  private key(row: number, col: number): string {
    return `${row},${col}`;
  }

  private reindex(): void {
    this.coverage.clear();
    this.merges.forEach((m, i) => {
      for (let r = m.row; r < m.row + m.rowspan; r++) {
        for (let c = m.col; c < m.col + m.colspan; c++) {
          this.coverage.set(this.key(r, c), i);
        }
      }
    });
  }

  add(row: number, col: number, rowspan: number, colspan: number): void {
    if (rowspan < 1 || colspan < 1) {
      throw new Error(`Spans must be positive (got ${rowspan}x${colspan})`);
    }
    if (row < 0 || col < 0) {
      throw new Error(`Coordinates must be non-negative (got ${row},${col})`);
    }
    for (let r = row; r < row + rowspan; r++) {
      for (let c = col; c < col + colspan; c++) {
        if (this.coverage.has(this.key(r, c))) {
          throw new Error(`Cell (${r},${c}) is already merged`);
        }
      }
    }
    const index = this.merges.length;
    this.merges.push({ row, col, rowspan, colspan });
    for (let r = row; r < row + rowspan; r++) {
      for (let c = col; c < col + colspan; c++) {
        this.coverage.set(this.key(r, c), index);
      }
    }
  }

  remove(row: number, col: number): boolean {
    const i = this.coverage.get(this.key(row, col));
    if (i === undefined) return false;
    this.merges.splice(i, 1);
    this.reindex(); // indices shifted, rebuild
    return true;
  }

  getMergeAt(row: number, col: number): Merge | null {
    const i = this.coverage.get(this.key(row, col));
    return i === undefined ? null : this.merges[i];
  }

  isAnchor(row: number, col: number): boolean {
    const m = this.getMergeAt(row, col);
    return m !== null && m.row === row && m.col === col;
  }

  getAll(): readonly Merge[] {
    return this.merges;
  }

  hasWallAbove(row: number, col: number): boolean {
    if (row === 0) return true; // grid boundary
    return !this.sameMerge(row, col, row - 1, col);
  }

  hasWallLeft(row: number, col: number): boolean {
    if (col === 0) return true;
    return !this.sameMerge(row, col, row, col - 1);
  }

  private sameMerge(r1: number, c1: number, r2: number, c2: number): boolean {
    const i1 = this.coverage.get(this.key(r1, c1));
    const i2 = this.coverage.get(this.key(r2, c2));
    // Both must be in a merge, and the same one
    return i1 !== undefined && i1 === i2;
  }

  mergeAcrossWall(
    orientation: "horizontal" | "vertical",
    row: number,
    col: number
  ): boolean {
    const [r2, c2] = orientation === "horizontal"
      ? [row - 1, col]  // cell above
      : [row, col - 1]; // cell to the left

    if (r2 < 0 || c2 < 0) return false; // wall is on grid boundary

    // Get the merge each side belongs to, or a synthetic 1x1 if unmerged
    const mergeA = this.getMergeAt(row, col)
      ?? { row, col, rowspan: 1, colspan: 1 };
    const mergeB = this.getMergeAt(r2, c2)
      ?? { row: r2, col: c2, rowspan: 1, colspan: 1 };

    // Already in the same merge — wall isn't actually there
    if (mergeA === mergeB) return false;

    // Bounding rectangle of the two merges
    const rowStart = Math.min(mergeA.row, mergeB.row);
    const colStart = Math.min(mergeA.col, mergeB.col);
    const rowEnd = Math.max(
      mergeA.row + mergeA.rowspan,
      mergeB.row + mergeB.rowspan
    );
    const colEnd = Math.max(
      mergeA.col + mergeA.colspan,
      mergeB.col + mergeB.colspan
    );

    // Check that every cell in the bounding rectangle is either unmerged
    // or part of mergeA or mergeB. Anything else means we'd split a third merge.
    const indexA = this.coverage.get(this.key(mergeA.row, mergeA.col));
    const indexB = this.coverage.get(this.key(mergeB.row, mergeB.col));
    for (let r = rowStart; r < rowEnd; r++) {
      for (let c = colStart; c < colEnd; c++) {
        const i = this.coverage.get(this.key(r, c));
        if (i !== undefined && i !== indexA && i !== indexB) {
          return false; // would partially overlap a third merge
        }
      }
    }

    // Safe to proceed. Remove the two source merges and add the union.
    // Remove in descending index order so splices don't invalidate the other index.
    const toRemove = [indexA, indexB]
      .filter((i): i is number => i !== undefined)
      .sort((a, b) => b - a);
    for (const i of toRemove) {
      this.merges.splice(i, 1);
    }

    this.merges.push({
      row: rowStart,
      col: colStart,
      rowspan: rowEnd - rowStart,
      colspan: colEnd - colStart,
    });
    this.reindex();
    return true;
  }

  /**
 * Split a merge along the wall the user clicked.
 * `orientation` is 'horizontal' for a wall above cell (row, col),
 * 'vertical' for a wall to the left of (row, col).
 * Returns true if a split happened, false if there was nothing to split
 * (e.g., the wall is on the merge's boundary or no merge contains it).
 */
  splitAcrossWall(
    orientation: "horizontal" | "vertical",
    row: number,
    col: number
  ): boolean {
    // The wall sits between two cells. Find the merge containing the cell
    // on the "inside" of the click. Both sides should be in the same merge
    // for this to be a split (otherwise the wall already exists).
    const [r2, c2] = orientation === "horizontal"
      ? [row - 1, col]
      : [row, col - 1];

    if (r2 < 0 || c2 < 0) return false; // boundary wall

    const indexA = this.coverage.get(this.key(row, col));
    const indexB = this.coverage.get(this.key(r2, c2));

    // Wall is only "internal to a merge" if both sides are in the same merge
    if (indexA === undefined || indexA !== indexB) return false;

    const merge = this.merges[indexA];
    const { row: r0, col: c0, rowspan, colspan } = merge;
    const r1 = r0 + rowspan;
    const c1 = c0 + colspan;

    // Remove the original merge
    this.merges.splice(indexA, 1);

    // Add the two halves. Skip any half that would be 1x1 — those are just
    // unmerged cells and don't need to be tracked.
    const addIfMulti = (m: Merge) => {
      if (m.rowspan > 1 || m.colspan > 1) this.merges.push(m);
    };

    if (orientation === "horizontal") {
      // Split into top half [r0, row) and bottom half [row, r1)
      addIfMulti({ row: r0, col: c0, rowspan: row - r0, colspan });
      addIfMulti({ row, col: c0, rowspan: r1 - row, colspan });
    } else {
      // Split into left half [c0, col) and right half [col, c1)
      addIfMulti({ row: r0, col: c0, rowspan, colspan: col - c0 });
      addIfMulti({ row: r0, col, rowspan, colspan: c1 - col });
    }

    this.reindex();
    return true;
  }

  /**
 * Merge a selection of cells into a single rectangular merge.
 * The bounding rectangle of the selection is computed, then expanded to
 * fully contain any existing merges it partially overlaps. The resulting
 * rectangle becomes a single merge, replacing any merges it covers.
 *
 * Returns the resulting merge on success, or null if the selection is empty.
 */
  mergeSelection(cells: { row: number; col: number }[]): Merge | null {
    if (cells.length === 0) return null;

    // Initial bounding rectangle of the selected cells
    let rowStart = Infinity;
    let colStart = Infinity;
    let rowEnd = -Infinity; // exclusive
    let colEnd = -Infinity; // exclusive

    for (const { row, col } of cells) {
      if (row < rowStart) rowStart = row;
      if (col < colStart) colStart = col;
      if (row + 1 > rowEnd) rowEnd = row + 1;
      if (col + 1 > colEnd) colEnd = col + 1;
    }

    // Expand the bounding rectangle to fully contain any merges it touches.
    // Each expansion may bring new merges into the rectangle, so iterate
    // until the rectangle is stable.
    let changed = true;
    while (changed) {
      changed = false;
      for (const m of this.merges) {
        const mRowEnd = m.row + m.rowspan;
        const mColEnd = m.col + m.colspan;
        // Does this merge intersect the current bounding rectangle?
        const intersects =
          m.row < rowEnd &&
          mRowEnd > rowStart &&
          m.col < colEnd &&
          mColEnd > colStart;
        if (!intersects) continue;
        // Expand to fully contain it
        if (m.row < rowStart) { rowStart = m.row; changed = true; }
        if (m.col < colStart) { colStart = m.col; changed = true; }
        if (mRowEnd > rowEnd) { rowEnd = mRowEnd; changed = true; }
        if (mColEnd > colEnd) { colEnd = mColEnd; changed = true; }
      }
    }

    // Find all merges fully contained in the final rectangle and remove them.
    // Sort indices descending so splices don't invalidate each other.
    const toRemove: number[] = [];
    this.merges.forEach((m, i) => {
      if (
        m.row >= rowStart &&
        m.col >= colStart &&
        m.row + m.rowspan <= rowEnd &&
        m.col + m.colspan <= colEnd
      ) {
        toRemove.push(i);
      }
    });
    toRemove.sort((a, b) => b - a);
    for (const i of toRemove) {
      this.merges.splice(i, 1);
    }

    // Add the unioned merge
    const result: Merge = {
      row: rowStart,
      col: colStart,
      rowspan: rowEnd - rowStart,
      colspan: colEnd - colStart,
    };

    // If it's degenerate (1x1), don't store it — but still return it so the
    // caller knows what "happened"
    if (result.rowspan > 1 || result.colspan > 1) {
      this.merges.push(result);
    }
    this.reindex();
    return result;
  }

  /**
 * Unmerge any merges that overlap with the selection.
 * Any merge containing at least one selected cell is removed entirely
 * (not partially split — the whole merge dissolves into 1x1 cells).
 *
 * Returns the array of merges that were removed.
 */
  unmergeSelection(cells: { row: number; col: number }[]): Merge[] {
    if (cells.length === 0) return [];

    // Collect indices of merges to remove. Use a Set since multiple selected
    // cells may belong to the same merge.
    const indicesToRemove = new Set<number>();
    for (const { row, col } of cells) {
      const i = this.coverage.get(this.key(row, col));
      if (i !== undefined) indicesToRemove.add(i);
    }

    if (indicesToRemove.size === 0) return [];

    // Capture the removed merges for the return value, then splice in
    // descending index order so earlier splices don't invalidate later indices.
    const sortedIndices = [...indicesToRemove].sort((a, b) => b - a);
    const removed: Merge[] = sortedIndices.map((i) => this.merges[i]);
    for (const i of sortedIndices) {
      this.merges.splice(i, 1);
    }

    this.reindex();
    return removed;
  }

  toJSON(): Merge[] {
    return this.merges;
  }
}

export { MergeStore };
